import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useField } from 'formik';
import { BoxProps, Flex, Box } from 'reflexbox';
import styled from 'styled-components';
import { mix } from 'polished';
import moment from 'moment';
import { parseInt } from 'lodash';

import { CashIcon, CloseIcon, PencilIcon, RulerIcon } from '@treams/treams-icons';
import {
	Card,
	Color,
	Heading,
	IconButton,
	NumericInput,
	Paragraph,
	Skeleton,
	Todo,
	Button,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';

import EditSubgoalCard from './EditSubgoalCard';

const Strikethrough = styled.span<{ orange?: boolean }>`
	background: ${(props) =>
		mix(
			0.75,
			props.theme.colors.white,
			props.orange ? props.theme.colors.yellow : props.theme.colors.green,
		)};
	color: ${(props) => (props.orange ? props.theme.colors.yellow : props.theme.colors.green)};
`;

const SubgoalIcon = styled(Box)<{ completed: boolean }>`
	svg {
		display: block;

		path {
			${(props) =>
				props.completed &&
				`
			fill: ${props.theme.colors.green};
		`}
		}
	}
`;

export default Skeleton(
	function SubgoalCard(props: { subgoalID: string; editable?: boolean; goalCompleted?: boolean }) {
		const { t } = useTranslation();
		const { localize } = useLocalize();

		const [{ value }, , { setValue }] = useField<graphql.GoalChecklistItem>(
			`subgoals.${props.subgoalID}`,
		);

		const [{ value: subgoalIDs }, , { setValue: setSubgoalIDs }] = useField<string[]>('subgoalIDs');

		const isDue =
			moment(value.dueDate).isBefore(moment()) && value.status === graphql.ChecklistItemStatus.Todo;

		const [edit, setEdit] = useState(false);
		const [progressValue, setProgressValue] = useState<number | undefined>(value.progress.value);

		if (edit) {
			return <EditSubgoalCard subgoalID={props.subgoalID} onDismiss={() => setEdit(false)} />;
		}

		return (
			<Card>
				<Flex alignItems="center">
					{value.progress.type === graphql.GoalChecklistItemType.Checkbox && (
						<Todo
							checked={value.progress.value === 1}
							late={value.progress.value !== 1 && props.goalCompleted}
							mr={2}
							flexGrow={0}
							flexShrink={0}
							onChange={
								props.editable
									? (checked) =>
											setValue({
												...value,
												progress: {
													...value.progress,
													value: checked ? 1 : 0,
												},
												// Only to be used for local reference. Should not be sent to server.
												completedAt: checked ? new Date() : undefined,
											})
									: undefined
							}
						/>
					)}
					{value.progress.type === graphql.GoalChecklistItemType.Currency && (
						<SubgoalIcon width={24} mr={2} completed={!!value.completedAt}>
							<CashIcon />
						</SubgoalIcon>
					)}
					{value.progress.type === graphql.GoalChecklistItemType.Range && (
						<SubgoalIcon width={24} mr={2} completed={!!value.completedAt}>
							<RulerIcon />
						</SubgoalIcon>
					)}
					<Box flexGrow={1} flexShrink={1}>
						{(value.dueDate || value.completedAt || value.createdAt) && (
							<Paragraph>
								{value.dueDate && (
									<Color color={isDue ? 'red' : 'slateBlue'}>
										{t('subgoals.card.dueDate', { dueDate: moment(value.dueDate).format('LL') })}
									</Color>
								)}

								{value.dueDate && ' · '}

								{value.completedAt
									? t('subgoals.card.completedAt', {
											completedAt: moment(value.completedAt).format('LL'),
									  })
									: value.createdAt &&
									  t('subgoals.card.createdAt', {
											date: moment(value.createdAt).format('LL'),
									  })}
							</Paragraph>
						)}

						<Heading>
							{value.completedAt ? (
								<Strikethrough>{localize(value.title)?.text}</Strikethrough>
							) : props.goalCompleted ? (
								<Strikethrough orange>{localize(value.title)?.text}</Strikethrough>
							) : (
								localize(value.title)?.text
							)}
						</Heading>
					</Box>

					{(value.progress.type === graphql.GoalChecklistItemType.Currency ||
						value.progress.type === graphql.GoalChecklistItemType.Range ||
						value.progress.type === graphql.GoalChecklistItemType.Percentage) && (
						<Flex mr={3} alignItems="center">
							<Box width={140} mr={3}>
								<Flex>
									{props.editable ? (
										<NumericInput
											prefix={
												value.progress.type === graphql.GoalChecklistItemType.Currency
													? '€'
													: undefined
											}
											value={String(progressValue)}
											onChange={(newValue) => setProgressValue(parseInt(newValue))}
										/>
									) : (
										(progressValue ?? 0).toString()
									)}
								</Flex>
							</Box>
							<Flex alignItems="center">
								<Box mr={2}>
									<Paragraph>/</Paragraph>
								</Box>

								<Box>
									{value.progress.type === graphql.GoalChecklistItemType.Currency && <>&euro;</>}
								</Box>
								{value.progress.upper && value.progress.upper.toLocaleString('nl-NL')}
							</Flex>
						</Flex>
					)}

					{props.editable &&
						(progressValue !== undefined &&
						value.progress.upper !== undefined &&
						progressValue !== value.progress.value &&
						value.progress.type !== graphql.GoalChecklistItemType.Checkbox ? (
							<Button
								title={t('goals.subgoals.save.button')}
								small
								onClick={() =>
									setValue({
										...value,
										progress: { ...value.progress, value: progressValue },
										completedAt: progressValue >= value.progress.upper! ? new Date() : undefined,
									})
								}
								width={72}
								ml={2}
							/>
						) : (
							<>
								<IconButton
									onClick={() => setEdit(true)}
									icon={<PencilIcon />}
									ml={2}
									flexGrow={0}
									flexShrink={0}
								/>
								<IconButton
									type="destructive"
									onClick={() => {
										setSubgoalIDs(subgoalIDs.filter((id) => id !== props.subgoalID));
									}}
									icon={<CloseIcon />}
									ml={2}
									flexGrow={0}
									flexShrink={0}
								/>
							</>
						))}
				</Flex>
			</Card>
		);
	},
	function SubgoalCardSkeleton(props: BoxProps) {
		return (
			<Card.Skeleton {...props}>
				<Heading.Skeleton mb={1} />
				<Paragraph.Skeleton lines={1} />
			</Card.Skeleton>
		);
	},
);
